/**
 * Piezas de UI compartidas por las pantallas hub de sucursal
 * (Reglas del POS, Dispositivos). Antes vivían duplicadas como
 * localStyles dentro de BranchPosOpsScreen.
 */
import React from 'react';
import { StyleSheet, Text } from 'react-native';
import { settingsStyles as styles } from '../settingsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { SoftPressable } from '../../../shared/ui/SoftPressable';

export function SettingsSaveCta({
  label,
  saving,
  onPress,
  disabled,
  layoutWide,
  variant = 'primary',
  testID,
}: {
  label: string;
  saving?: boolean;
  onPress: () => void;
  disabled?: boolean;
  layoutWide: boolean;
  variant?: 'primary' | 'secondary';
  testID?: string;
}) {
  const isPrimary = variant === 'primary';
  return (
    <SoftPressable
      style={[
        isPrimary ? hubStyles.ctaPrimary : hubStyles.ctaSecondary,
        layoutWide ? hubStyles.ctaDesk : hubStyles.ctaMobile,
        (disabled || saving) && styles.disabled,
      ]}
      onPress={onPress}
      disabled={disabled || saving}
      accessibilityRole="button"
      testID={testID}
    >
      <Text style={isPrimary ? hubStyles.ctaPrimaryText : hubStyles.ctaSecondaryText}>
        {saving ? 'Guardando…' : label}
      </Text>
    </SoftPressable>
  );
}

/** Badge semántico con dot — estados del agente/dispositivo. */
export function StatusBadge({
  tone,
  label,
  testID,
}: {
  tone: 'ok' | 'warn' | 'muted';
  label: string;
  testID?: string;
}) {
  const colors = {
    ok: { bg: '#F0FDF4', border: 'rgba(21,128,61,.28)', ink: ticketColors.ok },
    warn: { bg: ticketColors.warnBg, border: ticketColors.warnBorder, ink: ticketColors.warn },
    muted: { bg: ticketColors.tile, border: ticketColors.ruleSoft, ink: ticketColors.ink3 },
  }[tone];
  return (
    <Text
      style={[hubStyles.badge, { backgroundColor: colors.bg, borderColor: colors.border, color: colors.ink }]}
      testID={testID}
    >
      <Text style={{ color: colors.ink }}>● </Text>
      {label}
    </Text>
  );
}

/** Aviso contextual dentro de una card (fallback a vista previa, etc). */
export function SettingsNotice({
  tone,
  children,
  testID,
}: {
  tone: 'warn' | 'ok';
  children: React.ReactNode;
  testID?: string;
}) {
  const warn = tone === 'warn';
  return (
    <Text
      style={[
        hubStyles.notice,
        warn
          ? { backgroundColor: ticketColors.warnBg, borderColor: ticketColors.warnBorder }
          : { backgroundColor: '#F0FDF4', borderColor: 'rgba(21,128,61,.28)' },
      ]}
      testID={testID}
    >
      {children}
    </Text>
  );
}

export const hubStyles = StyleSheet.create({
  stack: {
    gap: 10,
    alignSelf: 'stretch',
  },
  controlBlock: {
    gap: 8,
    marginTop: spacing.sm,
  },
  hintSecondary: {
    fontSize: 13,
    lineHeight: 18,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
  },
  hintMuted: {
    fontSize: 12,
    lineHeight: 17,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink3,
  },
  inputTicket: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    minHeight: 44,
  },
  inputBlockWide: {
    maxWidth: 480,
    alignSelf: 'flex-start',
    width: '100%',
  },
  inputsStack: {
    gap: spacing.sm,
  },
  inputsRowWide: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignSelf: 'stretch',
  },
  inputCol: {
    flex: 1,
    minWidth: 0,
    gap: 8,
  },
  ctaPrimary: {
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaSecondary: {
    backgroundColor: ticketColors.paper2,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  ctaDesk: {
    minHeight: 42,
    borderRadius: 10,
    paddingHorizontal: spacing.lg,
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
  },
  ctaMobile: {
    minHeight: 48,
    borderRadius: 14,
    paddingHorizontal: spacing.lg,
    alignSelf: 'stretch',
    marginTop: spacing.xs,
  },
  ctaPrimaryText: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    color: ticketColors.goldInk,
  },
  ctaSecondaryText: {
    fontSize: 15,
    lineHeight: 20,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  badge: {
    fontSize: 12,
    lineHeight: 16,
    fontWeight: '700',
    fontFamily: ticketFonts.sans,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 3,
    overflow: 'hidden',
  },
  notice: {
    fontSize: 12.5,
    lineHeight: 18,
    fontWeight: '500',
    fontFamily: ticketFonts.sans,
    color: ticketColors.ink2,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
});
